
import React, { useState, useEffect } from 'react';
import { Shield, Activity, Users, FileText, RefreshCw, AlertTriangle, Monitor, LogOut } from 'lucide-react';
import { api } from '../lib/api';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function AdminPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        activeSensors: 3,
        alertsToday: 0,
        activeUsers: 0,
        systemHealth: '98%'
    });

    const [adminUser, setAdminUser] = useState(null);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPass, setLoginPass] = useState('');
    const [loginError, setLoginError] = useState('');

    useEffect(() => {
        if (adminUser) fetchData();
    }, [activeTab, adminUser]);

    const handleAdminLogin = (e) => {
        e.preventDefault();
        // Check hardcoded credentials for temporary MVP as requested
        // User = any email (or specific one, let's allow any valid email format for now as "my email")
        // Pass = admin123
        if (loginPass === 'admin123' && loginEmail.includes('@')) {
            setAdminUser({ email: loginEmail });
        } else {
            setLoginError('Credenciales incorrectas');
        }
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'logs') {
                const res = await api.audit.getLogs();
                if (res.data) setLogs(res.data);
            }
            if (activeTab === 'users' || activeTab === 'overview') {
                const res = await api.auth.getUsers();
                if (res.data) {
                    setUsers(res.data);
                    setStats(prev => ({ ...prev, activeUsers: res.data.length }));
                }
            }
            if (activeTab === 'alerts' || activeTab === 'overview') {
                const res = await api.alerts.getAll(); // Ensure API supports getAll without args or defaults
                if (res.data) {
                    setAlerts(res.data);
                    setStats(prev => ({ ...prev, alertsToday: res.data.length }));
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (!adminUser) {
        return (
            <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: 'white' }}>
                <form onSubmit={handleAdminLogin} className="card glass-effect" style={{ width: '100%', maxWidth: '400px', padding: 40 }}>
                    <div style={{ textAlign: 'center', marginBottom: 30 }}>
                        <Shield size={48} color="#f59e0b" />
                        <h2>Acceso Administrativo</h2>
                    </div>
                    <div className="form-group">
                        <label>Usuario (Correo)</label>
                        <input className="input-text" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
                    </div>
                    <div className="form-group" style={{ marginTop: 15 }}>
                        <label>Contraseña Panel</label>
                        <input className="input-text" type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} required />
                    </div>
                    {loginError && <p style={{ color: '#ef4444', marginTop: 10 }}>{loginError}</p>}
                    <button type="submit" className="btn btn-primary full-width" style={{ marginTop: 25 }}>Entrar</button>
                </form>
            </div>
        )
    }

    return (
        <div style={{ display: 'flex', height: '100vh', background: '#0f172a', color: 'white' }}>
            {/* Sidebar */}
            <div style={{ width: '250px', background: '#1e293b', padding: '20px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40, color: '#f59e0b' }}>
                    <Shield size={32} />
                    <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Admin Console</h2>
                </div>

                <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <NavBtn label="Panel General" icon={Activity} active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
                    <NavBtn label="Alertas" icon={AlertTriangle} active={activeTab === 'alerts'} onClick={() => setActiveTab('alerts')} />
                    <NavBtn label="Usuarios" icon={Users} active={activeTab === 'users'} onClick={() => setActiveTab('users')} />
                    <NavBtn label="Dispositivos" icon={Monitor} active={activeTab === 'devices'} onClick={() => setActiveTab('devices')} />
                    <NavBtn label="Auditoría / Logs" icon={FileText} active={activeTab === 'logs'} onClick={() => setActiveTab('logs')} />
                </nav>

                <button
                    onClick={() => navigate('/')}
                    style={{
                        marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 10,
                        background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 10
                    }}
                >
                    <LogOut size={20} /> Salir
                </button>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, padding: '30px', overflowY: 'auto' }}>
                <header style={{ marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1 style={{ margin: 0 }}>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
                    <button onClick={fetchData} className="btn-icon" title="Refrescar Datos">
                        <RefreshCw size={20} className={loading ? 'spin' : ''} />
                    </button>
                </header>

                {activeTab === 'overview' && (
                    <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
                        <StatCard title="Sensores Activos" value={stats.activeSensors} icon={Monitor} color="#10b981" />
                        <StatCard title="Alertas Totales" value={stats.alertsToday} icon={AlertTriangle} color="#ef4444" />
                        <StatCard title="Usuarios Registrados" value={stats.activeUsers} icon={Users} color="#3b82f6" />
                        <StatCard title="Salud Sistema" value={stats.systemHealth} icon={Activity} color="#8b5cf6" />
                    </div>
                )}

                {activeTab === 'users' && (
                    <div className="card glass-effect">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 15 }}>
                            <button onClick={fetchData} className="btn-icon" style={{ display: 'flex', gap: 5, alignItems: 'center', background: '#3b82f6', color: 'white', padding: '5px 15px', borderRadius: 5, cursor: 'pointer', border: 'none' }}>
                                <RefreshCw size={16} /> Actualizar Lista
                            </button>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                                    <th style={{ padding: 10 }}>Usuario</th>
                                    <th style={{ padding: 10 }}>Método</th>
                                    <th style={{ padding: 10 }}>Email</th>
                                    <th style={{ padding: 10 }}>UID</th>
                                    <th style={{ padding: 10 }}>Último Acceso</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.uid} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                                            {u.photoURL ?
                                                <img src={u.photoURL} alt="Avatar" style={{ width: 32, height: 32, borderRadius: '50%' }} /> :
                                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                                    {u.displayName ? u.displayName.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                            }
                                            {u.displayName || 'Sin Nombre'}
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {u.providerId === 'google.com' ? '🔵 Google' : '📧 Email'}
                                        </td>
                                        <td style={{ padding: 10 }}>{u.email}</td>
                                        <td style={{ padding: 10, fontFamily: 'monospace', fontSize: '0.8rem', color: '#94a3b8' }}>{u.uid}</td>
                                        <td style={{ padding: 10 }}>{new Date(u.lastSignInTime).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'alerts' && (
                    <div className="card glass-effect">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {alerts.length === 0 ? <p>No hay alertas registradas.</p> :
                                alerts.map(alert => (
                                    <div key={alert.id} style={{ padding: 15, background: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid #ef4444', borderRadius: 4 }}>
                                        <div style={{ fontWeight: 'bold', color: '#ef4444' }}>{alert.level.toUpperCase()}</div>
                                        <div>{alert.message}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 5 }}>
                                            {new Date(alert.timestamp).toLocaleString()} | Sector: {alert.sector}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )}

                {activeTab === 'logs' && (
                    <div className="logs-container glass-effect" style={{ padding: 15 }}>
                        {logs.map((log, idx) => (
                            <div key={idx} style={{
                                padding: '10px', background: 'rgba(255,255,255,0.03)',
                                borderLeft: `3px solid #3b82f6`, marginBottom: 10, fontSize: '0.9rem'
                            }}>
                                <div style={{ fontWeight: 'bold' }}>{log.action?.toUpperCase()}</div>
                                <div style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>{log.userId} - {new Date(log.timestamp).toLocaleString()}</div>
                                <pre style={{ margin: '5px 0 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>{JSON.stringify(log.details)}</pre>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function NavBtn({ label, icon: Icon, active, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 15px',
                background: active ? '#f59e0b' : 'transparent',
                border: 'none', borderRadius: 8, color: active ? 'white' : '#94a3b8',
                cursor: 'pointer', textAlign: 'left', fontWeight: active ? 'bold' : 'normal'
            }}
        >
            <Icon size={18} /> {label}
        </button>
    );
}

function StatCard({ title, value, icon: Icon, color }) {
    return (
        <div className="glass-effect" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 15 }}>
            <div style={{ padding: 12, borderRadius: 12, background: `${color}20`, color: color }}>
                <Icon size={28} />
            </div>
            <div>
                <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{title}</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{value}</div>
            </div>
        </div>
    );
}
